import { useEffect, useRef } from "react";

function Badges({ question }) {
  return (
    <>
      {question.isArchived && (
        <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
          (archivada)
        </span>
      )}
      {question.adminOnly && (
        <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          Solo admin
        </span>
      )}
    </>
  );
}

function QuestionCheck({ question, checked, onToggle, showBadges }) {
  return (
    <label className="flex items-start gap-2 py-1 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(question.questionId)}
        className="mt-0.5 w-4 h-4 shrink-0 accent-indigo-600"
      />
      <span className="text-sm text-gray-700 group-hover:text-gray-900">
        {question.label}
      </span>
      {showBadges && <Badges question={question} />}
    </label>
  );
}

// `indeterminate` no es un atributo de JSX: se asigna sobre el nodo del DOM.
function TriStateCheckbox({ checked, indeterminate, onChange }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="mt-0.5 w-4 h-4 shrink-0 accent-indigo-600"
    />
  );
}

// Una actividad-formulario aporta varias preguntas (una por campo reportable) y
// se agrupa bajo una cabecera tri-estado. Las de pregunta única (text/yesno) no
// tienen jerarquía: son un checkbox normal.
export default function ReportActivityGroup({
  template,
  questions,
  selected,
  onToggle,
  onSetMany,
}) {
  const isSingleQuestion =
    questions.length === 1 && questions[0].kind === "single";

  if (isSingleQuestion) {
    return (
      <QuestionCheck
        question={questions[0]}
        checked={selected.has(questions[0].questionId)}
        onToggle={onToggle}
        showBadges
      />
    );
  }

  const questionIds = questions.map((question) => question.questionId);
  const selectedCount = questionIds.filter((id) => selected.has(id)).length;
  const allSelected = selectedCount === questionIds.length;
  // Con un solo campo la cabecera sería un duplicado del propio campo.
  const hasHeaderCheckbox = questions.length > 1;

  const header = (
    <span className="text-sm font-medium text-gray-900">{template.name}</span>
  );

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        {hasHeaderCheckbox ? (
          <label className="flex items-start gap-2 cursor-pointer">
            <TriStateCheckbox
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={() => onSetMany(questionIds, !allSelected)}
            />
            {header}
          </label>
        ) : (
          header
        )}
        <Badges question={questions[0]} />
        {hasHeaderCheckbox && (
          <span className="shrink-0 text-xs text-gray-500">
            {selectedCount}/{questionIds.length}
          </span>
        )}
      </div>

      <div className={`mt-0.5 ${hasHeaderCheckbox ? "pl-6" : "pl-5"}`}>
        {questions.map((question) => (
          <QuestionCheck
            key={question.questionId}
            question={question}
            checked={selected.has(question.questionId)}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
}
